import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useAgent } from '../../contexts/AgentContext';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import { ReactComponent as CategoryAllIcon } from '../../icons/category-all.svg';
import { ReactComponent as CategoryTravelIcon } from '../../icons/category-travel.svg';
import { ReactComponent as CategoryBookingIcon } from '../../icons/category-booking.svg';
import { ReactComponent as CategoryPersonalIcon } from '../../icons/category-personal.svg';
import { ReactComponent as CategoryAdminIcon } from '../../icons/category-admin.svg';
import type {
  AutopilotTemplate,
  AutopilotTemplateCategory,
  GetAutopilotTemplatesResponse,
  ActivateAutopilotTemplateResponse,
} from '@dreamai/types';
import MobileNavToggle from './MobileNavToggle';
import './agent-home-page.scss';

const CATEGORY_FILTERS: {
  label: string;
  value: AutopilotTemplateCategory | 'All';
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
}[] = [
  { label: 'All', value: 'All', icon: CategoryAllIcon },
  { label: 'Travel', value: 'Travel', icon: CategoryTravelIcon },
  { label: 'Booking', value: 'Booking', icon: CategoryBookingIcon },
  { label: 'Personal', value: 'Personal', icon: CategoryPersonalIcon },
  { label: 'Admin', value: 'Admin', icon: CategoryAdminIcon },
];

type SidebarTab = 'autopilots' | 'skills';

const AgentHomePage: React.FC = () => {
  const { agents } = useAgent();
  const { getAccessTokenSilently } = useAuth0();
  const [activeCategory, setActiveCategory] = useState<
    AutopilotTemplateCategory | 'All'
  >('All');
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('autopilots');
  const [templates, setTemplates] = useState<AutopilotTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<AutopilotTemplate | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [activating, setActivating] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  const primaryAgent = useMemo(() => {
    return (
      agents.find((a) => (a.agent_type as string) === 'Personal Assistant') ||
      agents[0]
    );
  }, [agents]);

  const loadTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);
      const token = await getAccessTokenSilently();
      const response = await api.get<GetAutopilotTemplatesResponse>(
        '/autopilot-templates',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      console.error('Error fetching autopilot templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const filteredTemplates = useMemo(() => {
    const popular = templates.filter((t) => t.is_popular);
    if (activeCategory === 'All') return popular;
    return popular.filter((t) => t.category === activeCategory);
  }, [activeCategory, templates]);

  const handleSelectTemplate = (template: AutopilotTemplate) => {
    setSelectedTemplate(template);
    setFormValues({});
  };

  const handleBack = () => {
    setSelectedTemplate(null);
    setFormValues({});
  };

  const resolveDescription = (text: string): string => {
    if (!primaryAgent) return text;
    return text.replace(
      /\{agent_nickname\}/g,
      primaryAgent.nickname || primaryAgent.name || 'your secretary',
    );
  };

  const handleActivate = async () => {
    if (!selectedTemplate || !primaryAgent) return;
    try {
      setActivating(true);
      const token = await getAccessTokenSilently();
      await api.post<ActivateAutopilotTemplateResponse>(
        `/autopilot-templates/${selectedTemplate.id}/activate`,
        { agentId: primaryAgent.id, formValues },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      handleBack();
    } catch (err) {
      console.error('Error activating autopilot template:', err);
    } finally {
      setActivating(false);
    }
  };

  if (!primaryAgent) return null;

  return (
    <div className='agent-home-page inset-white-backed'>
      <MobileNavToggle />
      {/* Left Column - Agent Profile */}
      <div className='agent-sidebar'>
        <div className='agent-header'>
          <div className='agent-profile'>
            {primaryAgent.image_url && (
              <div className='avatar-holder'>
                <img
                  src={primaryAgent.image_url}
                  alt={primaryAgent.nickname || primaryAgent.name}
                  className='agent-avatar-inner'
                />
              </div>
            )}
            <div>
              <h2 className='agent-nickname'>
                {primaryAgent.nickname || primaryAgent.name}
              </h2>
              <p className='agent-type'>{primaryAgent.agent_type}</p>
            </div>
          </div>

          <div className='sidebar-tabs'>
            <button
              className={`sidebar-tab ${sidebarTab === 'autopilots' ? 'active' : ''}`}
              onClick={() => setSidebarTab('autopilots')}
            >
              Autopilots
            </button>
            <button
              className={`sidebar-tab ${sidebarTab === 'skills' ? 'active' : ''}`}
              onClick={() => setSidebarTab('skills')}
            >
              Skills
            </button>
          </div>
        </div>

        {primaryAgent.email && (
          <div className='agent-contact'>
            <div className='contact-item'>
              <span className='contact-label'>Email</span>
              <span className='contact-value'>{primaryAgent.email}</span>
            </div>
          </div>
        )}
      </div>

      {/* Right Column - Content */}
      <div className='agent-content'>
        {selectedTemplate ? (
          <div className='template-detail'>
            <button className='back-button' onClick={handleBack}>
              ← Back
            </button>

            <h2 className='template-name'>{selectedTemplate.name}</h2>

            <div className='template-section'>
              <h4 className='template-section-title'>Triggers</h4>
              <p className='template-section-content'>
                {selectedTemplate.triggers}
              </p>
            </div>

            <div className='template-section'>
              <h4 className='template-section-title'>Description</h4>
              <p className='template-section-content'>
                {resolveDescription(selectedTemplate.description)}
              </p>
            </div>

            {selectedTemplate.requirements.length > 0 && (
              <div className='template-section'>
                <h4 className='template-section-title'>Requirements</h4>
                <ul className='requirements-list'>
                  {selectedTemplate.requirements.map((req, i) => (
                    <li key={i} className='requirement-item'>
                      <span className='requirement-icon'>
                        {req.type === 'connected_email' && primaryAgent.email
                          ? '✓'
                          : '○'}
                      </span>
                      <span>{req.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedTemplate.time_estimates.length > 0 && (
              <div className='template-section'>
                <h4 className='template-section-title'>Time</h4>
                <ul className='time-estimates-list'>
                  {selectedTemplate.time_estimates.map((est, i) => (
                    <li key={i}>
                      {est.label}
                      {est.type === 'ai' && ' (AI)'}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedTemplate.forms && selectedTemplate.forms.length > 0 && (
              <div className='template-section'>
                <h4 className='template-section-title'>Setup</h4>
                {selectedTemplate.forms.map((form) => (
                  <div key={form.name} className='template-form-field'>
                    <label htmlFor={form.name}>{form.label}</label>
                    {form.type === 'textarea' ? (
                      <textarea
                        id={form.name}
                        placeholder={form.placeholder}
                        value={formValues[form.name] || ''}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [form.name]: e.target.value,
                          }))
                        }
                        rows={6}
                      />
                    ) : (
                      <input
                        id={form.name}
                        type='text'
                        placeholder={form.placeholder}
                        value={formValues[form.name] || ''}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            [form.name]: e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              className='activate-button'
              onClick={handleActivate}
              disabled={activating}
            >
              {activating ? 'Activating...' : 'Turn on autopilot'}
            </button>
          </div>
        ) : (
          <>
            {/* Category Filter Tabs */}
            <div className='category-filters'>
              {CATEGORY_FILTERS.map((filter) => {
                const Icon = filter.icon;
                return (
                  <button
                    key={filter.value}
                    className={`category-filter ${activeCategory === filter.value ? 'active' : ''}`}
                    onClick={() => setActiveCategory(filter.value)}
                  >
                    <Icon />
                    <span>{filter.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Popular Today */}
            <div className='popular-section'>
              <h3 className='section-title'>Popular today</h3>
              <div className='popular-grid'>
                {loadingTemplates ? (
                  <p className='loading-text'>Loading...</p>
                ) : (
                  filteredTemplates.map((template, index) => (
                    <button
                      key={template.id}
                      className={`popular-card ${index === 0 ? 'featured' : ''}`}
                      onClick={() => handleSelectTemplate(template)}
                    >
                      <span className='card-category'>
                        {template.category.toUpperCase()}
                      </span>
                      <span className='card-title'>{template.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgentHomePage;
